import { useState } from 'react'
import { Props_SnippetImage } from './Image.props'

type ImageFigureProps = {
    image: Props_SnippetImage['image']
    caption: Props_SnippetImage['caption']
    onImageLoad: () => void
}

const ImageFigure = ({ image, caption, onImageLoad }: ImageFigureProps) => {
    const imageHeight = image.height?.value
    const imageWidth = image.width?.value
    
    const inlineStyle: React.CSSProperties = {}
    
    if (imageHeight && imageWidth) {
        if (imageWidth > imageHeight) {
            inlineStyle.maxHeight = `${imageHeight}px`
            inlineStyle.height = 'auto'
        }
    }
    
    return (
        <figure className="snippet-image__figure">
            <img
                loading="lazy"
                src={image.src?.value}
                className="snippet-image__image"
                height={imageHeight}
                width={imageWidth}
                alt={image.alt?.value ? image.alt.value : ''}
                style={inlineStyle}
                onLoad={onImageLoad}
            />
            {caption.caption?.value && (
                <figcaption className="snippet-image__caption">
                    {caption.caption.value}
                </figcaption>
            )}
        </figure>
    )
}

const SnippetImage = (props: Props_SnippetImage) => {
    const { heading, image, caption, link } = props
    const [isImageLoaded, setIsImageLoaded] = useState(false)

    const classSnippetContainerSize = `snippet-image--container-size-${props.snippet.container?.value}`
    const classSnippetBackgroundColor = `snippet-image--bg--${props.snippet.backgroundColor?.value}`
    const classSnippetFontColor = `snippet-image--font-color-${props.snippet.fontColor?.value}`
    const classSnippetSpacing = props.snippet.spacing?.value === true ? 'snippet-image--container-spacing' : ''
    const classHeadingAlignment = heading.alignment?.value ? `snippet-image__heading--alignment-${heading.alignment.value}` : ''
    const classHeadingVisibility = heading.visibility?.value === true ? 'snippet-image__heading--visible' : 'snippet-image__heading--hidden'
    const classImagePosition = image.position?.value ? `snippet-image--image-position-${image.position.value}` : ''
    const classImageRounded = image.rounded?.value === true ? 'snippet-image--image-rounded' : ''
    const classCaptionPosition = caption.captionPosition?.value ? `snippet-image--caption-position-${caption.captionPosition.value}` : ''
    const classLazyLoaded = isImageLoaded ? 'snippet-image--loaded' : ''
    const classSnippet = [
        classSnippetContainerSize,
        classSnippetBackgroundColor,
        classSnippetFontColor,
        classSnippetSpacing,
        classHeadingAlignment,
        classImagePosition,
        classImageRounded,
        classCaptionPosition,
        classLazyLoaded
    ].filter(Boolean).join(' ')

    const componentImageFigure = <ImageFigure image={image} caption={caption} onImageLoad={() => setIsImageLoaded(true)} />
    
    return (
        <section className={`snippet-image ${classSnippet}`}>
            <div className="snippet-image__container">
                <h2 className={`snippet-image__heading ${classHeadingVisibility} ${classHeadingAlignment}`}>{heading.content.value}</h2>
                { link.linkHref?.value ? (
                    <a
                        href={link.linkHref.value}
                        target={link.linkTarget?.value ? '_blank' : '_self'}
                        className="snippet-image__link"
                        rel={link.linkTarget?.value ? 'noopener noreferrer' : undefined}
                    >
                        {componentImageFigure}
                    </a>
                ) : (
                    componentImageFigure
                )}
            </div>
        </section>
    )
}

export default SnippetImage